// =============================================================
// VARIANCE — The Executive Command Center
// File      : Compliance.tsx
// Location  : /home/Applications/variance/web/src/pages/Compliance.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Main Compliance Kanban with KPI MIS, Filters & Help.
// =============================================================

import { useState, useEffect, useMemo } from 'react';
import { Plus, ShieldCheck, AlertTriangle, FileText, LayoutList, LayoutDashboard, IndianRupee, HelpCircle, X, Filter, ArrowUpDown, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Transaction {
  trans_id: string;
  reg_uid: string;
  compliance_name: string;
  company_name: string;
  project_name: string | null;
  period: string;
  status: string;
  owner_name: string | null;
  statutory_due_date: string;
  risk_level: string;
  temp_id_ack_no: string | null;
  financial_outflow: number | null;
  remarks: string | null;
  trigger_date: string | null;
  internal_target_date: string | null;
  actual_completion_date: string | null;
}

interface Register {
  reg_uid: string;
  compliance_name: string;
  company_name: string;
  project_name: string | null;
}

const STAGES = ['Backlog', 'In Progress', 'Under Review', 'Filed'];

export function Compliance() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<Transaction[]>([]);
  const [registers, setRegisters] = useState<Register[]>([]);
  
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Transaction | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterCompany, setFilterCompany] = useState('All');
  const [filterCompliance, setFilterCompliance] = useState('All');

  const [sortField, setSortField] = useState<keyof Transaction>('statutory_due_date');
  const [sortAsc, setSortAsc] = useState(true);

  const [formData, setFormData] = useState({
    reg_uid: '', period: '', status: 'Backlog', custom_status: '',
    trigger_date: '', internal_target_date: '', statutory_due_date: '',
    temp_id_ack_no: '', financial_outflow: '', risk_level: 'Medium', remarks: ''
  });
  const [formError, setFormError] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [transRes, regRes] = await Promise.all([
        fetch('/api/compliance/transactions').then(r => r.json()),
        fetch('/api/compliance/register').then(r => r.json())
      ]);
      if (!transRes.error) setRecords(transRes);
      if (!regRes.error) setRegisters(regRes);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const processedRecords = useMemo(() => {
    let result = [...records];
    if (filterStatus !== 'All') result = result.filter(r => r.status === filterStatus);
    if (filterCompany !== 'All') result = result.filter(r => r.company_name === filterCompany);
    if (filterCompliance !== 'All') result = result.filter(r => r.compliance_name === filterCompliance);
    
    result.sort((a, b) => {
      const valA = a[sortField] || '';
      const valB = b[sortField] || '';
      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
    return result;
  }, [records, filterStatus, filterCompany, filterCompliance, sortField, sortAsc]);

  const requestSort = (field: keyof Transaction) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      const payload = {
        ...formData,
        status: formData.status === 'other' ? formData.custom_status : formData.status,
        financial_outflow: formData.financial_outflow ? parseFloat(formData.financial_outflow) : undefined,
      };

      const res = await fetch('/api/compliance/transactions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create record');
      
      setIsModalOpen(false);
      fetchData();
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    if (!draggedId) return;
    setRecords(prev => prev.map(r => r.trans_id === draggedId ? { ...r, status: newStatus } : r));
    try {
      await fetch(`/api/compliance/transactions/${draggedId}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
    } catch (error) { fetchData(); }
    setDraggedId(null);
  };

  const getRiskColor = (risk: string) => {
    if (risk === 'High' || risk === 'Critical') return '#EF4444';
    if (risk === 'Medium') return '#F59E0B';
    return '#22C55E';
  };

  const totalOutflow = records.reduce((sum, r) => sum + (r.financial_outflow || 0), 0);
  const totalOverdue = records.filter(r => r.status !== 'Filed' && new Date(r.statutory_due_date) < new Date()).length;

  return (
    <div className="space-y-6 max-w-[1600px] relative min-h-screen pt-4">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Compliance Center</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage statutory filings, approvals, and mandates.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/compliance/setup')} className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}><Settings size={18} /> Setup</button>
          <button onClick={() => setIsHelpOpen(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}><HelpCircle size={18} /> Help</button>
          <div className="flex p-1 rounded-lg border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
            <button onClick={() => setViewMode('table')} className={`p-2 rounded ${viewMode === 'table' ? 'bg-[var(--bg-panel-alt)] text-[var(--brand-accent)]' : 'text-gray-500'}`}><LayoutList size={18} /></button>
            <button onClick={() => setViewMode('kanban')} className={`p-2 rounded ${viewMode === 'kanban' ? 'bg-[var(--bg-panel-alt)] text-[var(--brand-accent)]' : 'text-gray-500'}`}><LayoutDashboard size={18} /></button>
          </div>
          <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white shadow-[0_0_15px_var(--brand-accent)]" style={{ backgroundColor: 'var(--brand-accent)' }}><Plus size={18} /> Add Filing</button>
        </div>
      </div>

      {/* KPI MIS STRIP */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)', backdropFilter: 'blur(10px)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Total Filings</span><FileText size={18} style={{ color: 'var(--brand-accent)' }} /></div>
          <div className="text-3xl font-bold font-mono text-white">{records.length}</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)', backdropFilter: 'blur(10px)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Pending</span><ShieldCheck size={18} style={{ color: '#F59E0B' }} /></div>
          <div className="text-3xl font-bold font-mono text-white">{records.filter(r => r.status !== 'Filed').length}</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)', backdropFilter: 'blur(10px)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Overdue</span><AlertTriangle size={18} style={{ color: '#EF4444' }} /></div>
          <div className="text-3xl font-bold font-mono text-red-500">{totalOverdue}</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)', backdropFilter: 'blur(10px)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Financial Outflow</span><IndianRupee size={18} style={{ color: 'var(--brand-accent)' }} /></div>
          <div className="text-3xl font-bold font-mono text-white">₹{totalOutflow.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
        <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--text-primary)' }}><Filter size={16} /> Filters:</div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-sm text-[var(--text-primary)]">
          <option value="All">All Statuses</option>
          {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-sm text-[var(--text-primary)]">
          <option value="All">All Companies</option>
          {Array.from(new Set(records.map(r => r.company_name))).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterCompliance} onChange={e => setFilterCompliance(e.target.value)} className="p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-sm text-[var(--text-primary)]">
          <option value="All">All Compliances</option>
          {Array.from(new Set(records.map(r => r.compliance_name))).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* TABLE VIEW */}
      {viewMode === 'table' && (
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase cursor-pointer" style={{ color: 'var(--text-secondary)' }} onClick={() => requestSort('compliance_name')}>Compliance <ArrowUpDown size={12} className="inline ml-1"/></th>
                <th className="p-4 text-xs font-semibold uppercase" style={{ color: 'var(--text-secondary)' }}>Company / Project</th>
                <th className="p-4 text-xs font-semibold uppercase" style={{ color: 'var(--text-secondary)' }}>Period</th>
                <th className="p-4 text-xs font-semibold uppercase text-center cursor-pointer" style={{ color: 'var(--text-secondary)' }} onClick={() => requestSort('statutory_due_date')}>Due Date <ArrowUpDown size={12} className="inline ml-1"/></th>
                <th className="p-4 text-xs font-semibold uppercase text-center" style={{ color: 'var(--text-secondary)' }}>Status</th>
                <th className="p-4 text-xs font-semibold uppercase text-center" style={{ color: 'var(--text-secondary)' }}>View</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? <tr><td colSpan={6} className="p-8 text-center text-gray-400">Loading...</td></tr> : processedRecords.map(r => (
                <tr key={r.trans_id} className="border-t hover:bg-[var(--bg-panel-alt)] transition-colors cursor-pointer" style={{ borderColor: 'var(--border-subtle)' }} onClick={() => setSelectedRecord(r)}>
                  <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{r.compliance_name}</td>
                  <td className="p-4 text-xs" style={{ color: 'var(--text-primary)' }}>{r.company_name} {r.project_name ? `> ${r.project_name}` : ''}</td>
                  <td className="p-4 text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{r.period}</td>
                  <td className="p-4 text-xs font-mono text-center" style={{ color: 'var(--text-primary)' }}>{r.statutory_due_date ? new Date(r.statutory_due_date).toLocaleDateString() : '-'}</td>
                  <td className="p-4 text-center"><span className="inline-flex px-2 py-1 rounded text-xs font-bold bg-[var(--bg-panel-alt)] text-[var(--text-primary)]">{r.status}</span></td>
                  <td className="p-4 text-center"><button className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-[var(--brand-accent)]"><FileText size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* KANBAN VIEW */}
      {viewMode === 'kanban' && (
        <div className="flex gap-4 overflow-x-auto pb-4 w-full">
          {STAGES.map(stage => {
            const stageRecs = processedRecords.filter(r => r.status === stage);
            return (
              <div key={stage} className="flex-shrink-0 w-[300px] md:w-80 flex flex-col rounded-xl" style={{ backgroundColor: 'var(--bg-panel-alt)', border: '1px solid var(--border-subtle)' }} onDragOver={e => e.preventDefault()} onDrop={e => handleDrop(e, stage)}>
                <div className="p-3 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
                  <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{stage}</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-secondary)' }}>{stageRecs.length}</span>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[150px]">
                  {stageRecs.map(r => (
                    <div key={r.trans_id} draggable onDragStart={() => setDraggedId(r.trans_id)} onClick={() => setSelectedRecord(r)} className="p-3 rounded-lg cursor-grab active:cursor-grabbing hover:border-[var(--brand-accent)] transition-colors" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
                      <div className="flex justify-between items-start mb-1">
                        <div className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{r.period}</div>
                        <div className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${getRiskColor(r.risk_level)}20`, color: getRiskColor(r.risk_level) }}>{r.risk_level}</div>
                      </div>
                      <div className="font-medium text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{r.compliance_name}</div>
                      <div className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{r.company_name} {r.project_name ? `> ${r.project_name}` : ''}</div>
                      <div className="flex justify-between items-center text-xs">
                        <span style={{ color: 'var(--text-secondary)' }}>{r.owner_name || 'Unassigned'}</span>
                        <span className="font-mono" style={{ color: '#EF4444' }}>{r.statutory_due_date ? new Date(r.statutory_due_date).toLocaleDateString() : '-'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* HELP DRAWER */}
      {isHelpOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="w-full max-w-md h-full flex flex-col shadow-2xl animate-slide-in-right" style={{ backgroundColor: 'var(--bg-panel)', borderLeft: '1px solid var(--border-subtle)' }}>
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><HelpCircle /> Compliance Guide</h2>
              <button onClick={() => setIsHelpOpen(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto">
              <div><h3 className="font-bold mb-2" style={{ color: 'var(--brand-accent)' }}>How this works</h3><p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>1. Go to <b>Setup</b> to define the Compliance Rules.<br/>2. Register the rule for a Company/Project.<br/>3. Click <b>Add Filing</b> here to create the monthly task.</p></div>
              <div><h3 className="font-bold mb-2" style={{ color: 'var(--brand-accent)' }}>The 4 Date Fields</h3><p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}><b>Trigger:</b> When the period starts.<br/><b>Internal Target:</b> Your self-imposed buffer.<br/><b>Statutory Due:</b> The legal deadline.<br/><b>Actual Completion:</b> Auto-filled when you move to Filed.</p></div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW DETAILS PANEL */}
      {selectedRecord && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="w-full max-w-md h-full flex flex-col shadow-2xl animate-slide-in-right" style={{ backgroundColor: 'var(--bg-panel)', borderLeft: '1px solid var(--border-subtle)' }}>
            <div className="p-6 border-b flex justify-between items-start" style={{ borderColor: 'var(--border-subtle)' }}>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{selectedRecord.compliance_name}</h2>
                <div className="text-sm mt-1" style={{ color: 'var(--brand-accent)' }}>{selectedRecord.period}</div>
              </div>
              <button onClick={() => setSelectedRecord(null)} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Filing Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Company</span><span style={{ color: 'var(--text-primary)' }}>{selectedRecord.company_name}</span></div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Project</span><span style={{ color: 'var(--text-primary)' }}>{selectedRecord.project_name || '-'}</span></div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Status</span><span style={{ color: 'var(--text-primary)' }}>{selectedRecord.status}</span></div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Temp ID / ARN</span><span className="font-mono" style={{ color: 'var(--text-primary)' }}>{selectedRecord.temp_id_ack_no || '-'}</span></div>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Timeline</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Trigger Date</span><span style={{ color: 'var(--text-primary)' }}>{selectedRecord.trigger_date ? new Date(selectedRecord.trigger_date).toLocaleDateString() : '-'}</span></div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Internal Target</span><span style={{ color: 'var(--text-primary)' }}>{selectedRecord.internal_target_date ? new Date(selectedRecord.internal_target_date).toLocaleDateString() : '-'}</span></div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Statutory Due</span><span style={{ color: 'var(--text-primary)' }}>{new Date(selectedRecord.statutory_due_date).toLocaleDateString()}</span></div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Actual Completion</span><span style={{ color: 'var(--text-primary)' }}>{selectedRecord.actual_completion_date ? new Date(selectedRecord.actual_completion_date).toLocaleDateString() : '-'}</span></div>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Financials & Notes</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}><span style={{ color: 'var(--text-secondary)' }}>Financial Outflow</span><span className="font-mono" style={{ color: 'var(--text-primary)' }}>₹{(selectedRecord.financial_outflow || 0).toLocaleString('en-IN')}</span></div>
                  <div className="pt-2"><span className="block mb-1" style={{ color: 'var(--text-secondary)' }}>Remarks</span><div className="p-3 rounded-lg bg-[var(--bg-panel-alt)] text-xs" style={{ color: 'var(--text-primary)' }}>{selectedRecord.remarks || 'No remarks.'}</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD TRANSACTION MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-10">
          <div className="w-full max-w-2xl p-8 rounded-xl shadow-2xl my-8" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Create Compliance Filing</h2>
            {formError && <div className="p-3 mb-4 rounded text-sm text-red-500 bg-red-500/10">{formError}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Select Compliance Register <span className="text-red-500">*</span></label>
                <select required value={formData.reg_uid} onChange={e => setFormData({...formData, reg_uid: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                  <option value="">-- Choose a registered compliance --</option>
                  {registers.map(r => (
                    <option key={r.reg_uid} value={r.reg_uid}>
                      {r.compliance_name} - {r.company_name} {r.project_name ? `(${r.project_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Period <span className="text-red-500">*</span></label>
                  <input required type="text" placeholder="e.g. Oct 2026" value={formData.period} onChange={e => setFormData({...formData, period: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                    <option value="Backlog">Backlog</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Filed">Filed / Completed</option>
                    <option value="other">+ Add New Status</option>
                  </select>
                  {formData.status === 'other' && <input required type="text" placeholder="Custom status" value={formData.custom_status} onChange={e => setFormData({...formData, custom_status: e.target.value})} className="w-full mt-2 p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-[var(--bg-panel-alt)] border border-[var(--border-subtle)]">
                <div>
                  <label className="flex items-center gap-1 text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Trigger Date <HelpCircle size={14} title="Day the compliance period starts" className="cursor-help"/></label>
                  <input type="date" value={formData.trigger_date} onChange={e => setFormData({...formData, trigger_date: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Internal Target <HelpCircle size={14} title="Your self-imposed buffer" className="cursor-help"/></label>
                  <input type="date" value={formData.internal_target_date} onChange={e => setFormData({...formData, internal_target_date: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Statutory Due <span className="text-red-500">*</span> <HelpCircle size={14} title="The legal deadline" className="cursor-help"/></label>
                  <input required type="date" value={formData.statutory_due_date} onChange={e => setFormData({...formData, statutory_due_date: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center gap-1 text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Financial Outflow (₹) <HelpCircle size={14} title="Tax or Fee paid" className="cursor-help"/></label>
                  <input type="number" placeholder="e.g. 50000" value={formData.financial_outflow} onChange={e => setFormData({...formData, financial_outflow: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Temp ID / ARN</label>
                  <input type="text" placeholder="e.g. AA123456789" value={formData.temp_id_ack_no} onChange={e => setFormData({...formData, temp_id_ack_no: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Risk Level</label>
                  <select value={formData.risk_level} onChange={e => setFormData({...formData, risk_level: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                    <option value="Critical">Critical</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Remarks</label>
                <textarea rows={2} placeholder="Any special instructions..." value={formData.remarks} onChange={e => setFormData({...formData, remarks: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
              </div>

              <div className="flex gap-3 pt-6 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 rounded font-medium bg-[var(--bg-panel-alt)] text-[var(--text-primary)]">Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Filing</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}